import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail, 
  confirmPasswordReset,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  fetchSignInMethodsForEmail
} from 'firebase/auth';

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  query,
  where,
  collection,
  getDocs
} from 'firebase/firestore';

import { httpsCallable } from 'firebase/functions';

import { auth, db, functions } from '../../services/firebase';

/**
 * Generate secure reset token
 * @returns {string} - Random reset token
 */
const generateResetToken = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15) + 
         Date.now().toString(36);
};

/**
 * Send custom welcome email (no Firebase verification)
 * @param {Object} user - Firebase user object
 * @returns {Promise<void>}
 */
const sendCustomVerificationEmail = async (user) => {
  console.log('🔥 sendCustomVerificationEmail called for:', user.email);
  try {
    // ONLY send our beautiful custom email - NO Firebase verification email
    await setDoc(doc(db, 'mail', `welcome_${user.uid}_${Date.now()}`), {
      to: [user.email],
      from: 'accounts@a-r-t-i-f-e-x.com',
      subject: 'Welcome to Artifex - Create Your Digital Card',
      html: `
        <div style="font-family: Georgia, serif; max-width: 600px; margin: 0 auto; padding: 40px 20px; background: #ffffff;">
          <div style="text-align: center; margin-bottom: 40px;">
            <h1 style="font-size: 32px; letter-spacing: 4px; color: #1a1a1a; margin: 0; font-weight: normal;">
              A R T I F E X
            </h1>
          </div>
          
          <h2 style="font-size: 24px; color: #1a1a1a; margin-bottom: 20px; font-weight: normal;">Welcome to Artifex</h2>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 20px;">
            Your account has been created successfully! You're now part of our curated design community.
          </p>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 20px;">
            Next step: Create your Digital Card - your aesthetic passport that learns your design preferences and personalizes your Artifex experience.
          </p>
          
          <div style="text-align: center; margin: 40px 0;">
            <a href="https://www.a-r-t-i-f-e-x.com/card-generator?welcome=true&email=${encodeURIComponent(user.email)}" style="
              background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
              color: white;
              padding: 18px 36px;
              text-decoration: none;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 500;
              letter-spacing: 2px;
              display: inline-block;
              text-transform: uppercase;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            ">Create Digital Card</a>
          </div>
          
          <p style="color: #666; font-size: 14px; line-height: 1.5; margin-top: 30px;">
            Your Digital Card will personalize content discovery, serve as your design identity, and can be used across platforms.
          </p>
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center;">
            <p style="font-size: 16px; color: #1a1a1a; margin: 0;">
              The Artifex Team
            </p>
          </div>
        </div>
      `
    });
    
    console.log('Custom welcome email queued for:', user.email);
  } catch (error) {
    console.error('Error sending custom welcome email:', error);
  }
};

/**
 * Send custom password reset email
 * @param {string} email - User email
 * @param {string} resetToken - Reset token
 * @returns {Promise<void>}
 */
const sendCustomPasswordResetEmail = async (email, resetToken) => {
  console.log('🔥 sendCustomPasswordResetEmail called for:', email);
  try {
    await setDoc(doc(db, 'mail', `reset_${resetToken}_${Date.now()}`), {
      to: [email],
      from: 'accounts@a-r-t-i-f-e-x.com',
      subject: 'Reset Your Artifex Password',
      html: `
        <div style="font-family: Georgia, serif; max-width: 600px; margin: 0 auto; padding: 40px 20px; background: #ffffff;">
          <div style="text-align: center; margin-bottom: 40px;">
            <h1 style="font-size: 32px; letter-spacing: 4px; color: #1a1a1a; margin: 0; font-weight: normal;">
              A R T I F E X
            </h1>
          </div>
          
          <h2 style="font-size: 24px; color: #1a1a1a; margin-bottom: 20px; font-weight: normal;">Reset Your Password</h2>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 20px;">
            We received a request to reset your Artifex password. Click the button below to create a new password:
          </p>
          
          <div style="text-align: center; margin: 40px 0;">
            <a href="https://www.a-r-t-i-f-e-x.com/reset-password?token=${resetToken}" style="
              background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
              color: white;
              padding: 18px 36px;
              text-decoration: none;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 500;
              letter-spacing: 2px;
              display: inline-block;
              text-transform: uppercase;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            ">Reset Password</a>
          </div>
          
          <p style="color: #666; font-size: 14px; line-height: 1.5; margin-top: 30px;">
            This password reset link will expire in 1 hour. If you didn't request a password reset, you can safely ignore this email.
          </p>
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center;">
            <p style="font-size: 16px; color: #1a1a1a; margin: 0;">
              The Artifex Team
            </p>
          </div>
        </div>
      `
    });
    
    console.log('Custom password reset email queued for:', email);
  } catch (error) {
    console.error('Error sending custom password reset email:', error);
    throw error;
  }
};

/**
 * Create a new user account with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {Object} userData - Additional user data
 * @returns {Promise<Object>} - Operation result
 */
export const createAccount = async (email, password, userData = {}) => {
  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Set display name if provided
    if (userData.displayName) {
      await updateProfile(user, {
        displayName: userData.displayName
      });
    }
    
    // Send custom welcome email (no Firebase verification)
    console.log('🔥 About to call sendCustomVerificationEmail');
    await sendCustomVerificationEmail(user);
   
    // Create user document in Firestore
    const userDocRef = doc(db, 'users', user.uid);
    
    // Prepare user data
    const userDocData = {
      uid: user.uid,
      email: user.email,
      displayName: userData.displayName || userData.username || '',
      username: userData.username || '',
      photoURL: userData.photoURL || '',
      bio: userData.bio || 'Design enthusiast',
      emailVerified: true, // Skip email verification for now
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      userRole: 'guest',
      approvedForUploads: false,
      curationSignature: userData.curationSignature || {
        preferredTags: [],
        avoidedTags: [],
        influences: [],
        materialBias: [],
        colorPalette: [],
        styleBias: [],
        scoreThreshold: 0.6,
        signalWeights: {},
      },
      history: userData.history || {
        likedItemIds: [],
        dismissedItemIds: [],
        viewedItemIds: [],
      },
      settings: userData.settings || {
        filterMode: 'global',
        public: false,
      }
    };
    
    // Save to Firestore
    await setDoc(userDocRef, userDocData);
    
    return {
      success: true,
      user,
      message: 'Account created! Check your email to create your Digital Card.'
    };
  } catch (error) {
    console.error('Error creating account:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/email-already-in-use') {
      return {
        success: false,
        error: 'This email is already in use. Please try a different email or log in.'
      };
    }
    
    if (error.code === 'auth/weak-password') {
      return {
        success: false,
        error: 'Please use a stronger password (at least 6 characters).'
      };
    }
    
    if (error.code === 'auth/invalid-email') {
      return {
        success: false,
        error: 'Invalid email address. Please check and try again.'
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} - Operation result
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Skip email verification check - allow all users to sign in
    
    // Get user data from Firestore
    const userDocRef = doc(db, 'users', user.uid);
    const userDocSnap = await getDoc(userDocRef);
    
    let userData = null;
    if (userDocSnap.exists()) {
      userData = userDocSnap.data();
      
      // Update last login timestamp
      await updateDoc(userDocRef, {
        lastLogin: serverTimestamp()
      });
    } else {
      // Create basic user document if it doesn't exist
      userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        emailVerified: true, // Set to true by default
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        userRole: 'guest',
        approvedForUploads: false,
        curationSignature: {
          preferredTags: [],
          avoidedTags: [],
          influences: [],
          materialBias: [],
          colorPalette: [],
          styleBias: [],
          scoreThreshold: 0.6,
          signalWeights: {},
        },
        history: {
          likedItemIds: [],
          dismissedItemIds: [],
          viewedItemIds: [],
        },
        settings: {
          filterMode: 'global',
          public: false,
        }
      };
      
      await setDoc(userDocRef, userData);
    }
    
    return {
      success: true,
      user,
      userData
    };
  } catch (error) {
    console.error('Error signing in:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      return {
        success: false,
        error: 'Invalid email or password. Please try again.'
      };
    }
    
    if (error.code === 'auth/too-many-requests') {
      return {
        success: false,
        error: 'Too many failed login attempts. Please try again later or reset your password.'
      };
    }
    
    if (error.code === 'auth/user-disabled') {
      return {
        success: false,
        error: 'This account has been disabled. Please contact support.'
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Sign out the current user
 * @returns {Promise<Object>} - Operation result
 */
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return {
      success: true
    };
  } catch (error) {
    console.error('Error signing out:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Send custom password reset email (replaces Firebase default)
 * @param {string} email - User email
 * @returns {Promise<Object>} - Operation result
 */
export const resetPassword = async (email) => {
  console.log('🔥 resetPassword called for:', email);
  
  try {
    console.log('Step 1: Generating reset token...');
    const resetToken = generateResetToken();
    console.log('✅ Token generated:', resetToken);
    
    console.log('Step 2: Creating passwordResets document...');
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 1);
    
    await setDoc(doc(db, 'passwordResets', resetToken), {
      email: email,
      expiresAt: expiresAt,
      createdAt: serverTimestamp(),
      used: false
    });
    console.log('✅ passwordResets document created');
    
    console.log('Step 3: Creating mail document...');
    await sendCustomPasswordResetEmail(email, resetToken);
    console.log('✅ Mail document created successfully');
    
    return {
      success: true,
      message: 'Password reset email sent. Please check your inbox.'
    };
  } catch (error) {
    console.error('❌ Error in resetPassword at step:', error);
    console.error('Error details:', error.code, error.message);
    return {
      success: false,
      error: 'Failed to send reset email. Please try again.'
    };
  }
};

/**
 * Verify reset token and get associated email
 * @param {string} token - Reset token
 * @returns {Promise<Object>} - Verification result
 */
export const verifyResetToken = async (token) => {
  try {
    const tokenDoc = await getDoc(doc(db, 'passwordResets', token));
    
    if (!tokenDoc.exists()) {
      return {
        success: false,
        error: 'Invalid reset token.'
      };
    }
    
    const tokenData = tokenDoc.data();
    
    // Check if token is expired
    if (tokenData.expiresAt.toDate() < new Date()) {
      return {
        success: false,
        error: 'Reset token has expired. Please request a new one.'
      };
    }
    
    // Check if token has been used
    if (tokenData.used) {
      return {
        success: false,
        error: 'Reset token has already been used.'
      };
    }
    
    return {
      success: true,
      email: tokenData.email
    };
  } catch (error) {
    console.error('Error verifying reset token:', error);
    return {
      success: false,
      error: 'Failed to verify reset token.'
    };
  }
};

/**
 * Complete password reset with token and new password - ACTUALLY WORKS VERSION
 * @param {string} token - Reset token
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - Operation result
 */
export const confirmPasswordResetWithToken = async (token, newPassword) => {
  try {
    // Call the Firebase Function that actually updates the password
    const updatePasswordFunction = httpsCallable(functions, 'updatePasswordWithToken');
    const result = await updatePasswordFunction({ token, newPassword });
    
    return {
      success: true,
      message: 'Password reset successful! You can now log in with your new password.'
    };
  } catch (error) {
    console.error('Error confirming password reset:', error);
    return {
      success: false,
      error: error.message || 'Failed to reset password. Please try again.'
    };
  }
};

/**
 * Complete password reset with code and new password (Firebase fallback)
 * @param {string} code - Password reset code from email
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - Operation result
 */
export const confirmPasswordResetWithCode = async (code, newPassword) => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
    return {
      success: true,
      message: 'Password reset successful. You can now log in with your new password.'
    };
  } catch (error) {
    console.error('Error confirming password reset:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/expired-action-code') {
      return {
        success: false,
        error: 'This password reset link has expired. Please request a new one.'
      };
    }
    
    if (error.code === 'auth/invalid-action-code') {
      return {
        success: false,
        error: 'This password reset link is invalid. It may have been used already.'
      };
    }
    
    if (error.code === 'auth/weak-password') {
      return {
        success: false,
        error: 'Please use a stronger password (at least 6 characters).'
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Resend verification email for a specific user (dummy function)
 * @param {Object} user - Firebase user object
 * @returns {Promise<Object>} - Operation result
 */
export const resendVerificationForUser = async (user) => {
  // Do nothing - verification disabled
  return {
    success: true,
    message: 'Email verification is disabled.'
  };
};

/**
 * Update user profile information
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} - Operation result
 */
export const updateUserProfile = async (profileData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in.'
      };
    }
    
    // Update profile in Firebase Auth
    if (profileData.displayName || profileData.photoURL) {
      await updateProfile(user, {
        displayName: profileData.displayName,
        photoURL: profileData.photoURL
      });
    }
    
    // Update profile in Firestore
    const userDocRef = doc(db, 'users', user.uid);
    
    // Remove any undefined values
    const updateData = Object.entries(profileData)
      .filter(([_, value]) => value !== undefined)
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});
      
    // Add timestamp
    updateData.updatedAt = serverTimestamp();
    
    await updateDoc(userDocRef, updateData);
    
    return {
      success: true,
      message: 'Profile updated successfully.'
    };
  } catch (error) {
    console.error('Error updating profile:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user's email address (requires recent authentication)
 * @param {string} newEmail - New email address
 * @returns {Promise<Object>} - Operation result
 */
export const updateUserEmail = async (newEmail) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in.'
      };
    }
    
    // Update email in Firebase Auth
    await updateEmail(user, newEmail);
    
    // Update email in Firestore
    const userDocRef = doc(db, 'users', user.uid);
    await updateDoc(userDocRef, {
      email: newEmail,
      emailVerified: true, // Keep as verified
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: 'Email updated successfully.'
    };
  } catch (error) {
    console.error('Error updating email:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/requires-recent-login') {
      return {
        success: false,
        error: 'This operation requires recent authentication. Please log out and log in again before retrying.',
        requiresReauthentication: true
      };
    }
    
    if (error.code === 'auth/email-already-in-use') {
      return {
        success: false,
        error: 'This email is already in use by another account.'
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user's password (requires recent authentication)
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - Operation result
 */
export const updateUserPassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in.'
      };
    }
    
    // Reauthenticate with current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update password
    await updatePassword(user, newPassword);
    
    return {
      success: true,
      message: 'Password updated successfully.'
    };
  } catch (error) {
    console.error('Error updating password:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/wrong-password') {
      return {
        success: false,
        error: 'Current password is incorrect.'
      };
    }
    
    if (error.code === 'auth/weak-password') {
      return {
        success: false,
        error: 'Please use a stronger password (at least 6 characters).'
      };
    }
    
    if (error.code === 'auth/requires-recent-login') {
      return {
        success: false,
        error: 'This operation requires recent authentication. Please log out and log in again before retrying.',
        requiresReauthentication: true
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete user account (requires recent authentication)
 * @param {string} password - Current password for verification
 * @returns {Promise<Object>} - Operation result
 */
export const deleteUserAccount = async (password) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in.'
      };
    }
    
    // Reauthenticate with password
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    
    // Delete user data from Firestore
    const userDocRef = doc(db, 'users', user.uid);
    await deleteDoc(userDocRef);
    
    // Delete user from Firebase Auth
    await deleteUser(user);
    
    return {
      success: true,
      message: 'Your account has been deleted.'
    };
  } catch (error) {
    console.error('Error deleting account:', error);
    
    // Handle specific error codes
    if (error.code === 'auth/wrong-password') {
      return {
        success: false,
        error: 'Password is incorrect.'
      };
    }
    
    if (error.code === 'auth/requires-recent-login') {
      return {
        success: false,
        error: 'This operation requires recent authentication. Please log out and log in again before retrying.',
        requiresReauthentication: true
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get user data from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User data or null
 */
export const getUserData = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      return userDocSnap.data();
    } else {
      console.log('No user data found for ID:', userId);
      return null;
    }
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

/**
 * Check if current user's email is verified
 * @returns {Promise<boolean>} - True if verified
 */
export const checkEmailVerified = async () => {
  // Always return true - skip verification
  return true;
};

/**
 * Check verification status and update Firestore
 * @param {Object} user - Firebase user object
 * @returns {Promise<Object>} - Verification result
 */
export const checkEmailVerification = async (user) => {
  try {
    // Always mark as verified
    const userDocRef = doc(db, 'users', user.uid);
    await updateDoc(userDocRef, {
      emailVerified: true,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      verified: true
    };
  } catch (error) {
    console.error('Error checking verification status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Reauthenticate with password
 * @param {string} password - Current password
 * @returns {Promise<Object>} - Operation result
 */
export const reauthenticateUser = async (password) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in.'
      };
    }
    
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error reauthenticating:', error);
    
    if (error.code === 'auth/wrong-password') {
      return {
        success: false,
        error: 'Password is incorrect.'
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if username is available
 * @param {string} username - Username to check
 * @returns {Promise<boolean>} - True if available
 */
export const checkUsernameAvailability = async (username) => {
  try {
    if (!username || username.length < 3) {
      return false;
    }
    
    // Only allow letters, numbers, underscores, and periods
    if (!/^[a-zA-Z0-9._]+$/.test(username)) {
      return false;
    }
    
    // Check if username exists in Firestore
    const usernameQuery = await getDoc(doc(db, 'usernames', username.toLowerCase()));
    
    return !usernameQuery.exists();
  } catch (error) {
    console.error('Error checking username:', error);
    return false;
  }
};

/**
 * Reserve username for user
 * @param {string} username - Username to reserve
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Operation result
 */
export const reserveUsername = async (username, userId) => {
  try {
    await setDoc(doc(db, 'usernames', username.toLowerCase()), {
      uid: userId,
      createdAt: serverTimestamp()
    });
    return {
     success: true
   };
 } catch (error) {
   console.error('Error reserving username:', error);
   return {
     success: false,
     error: error.message
   };
 }
};

const authService = {
 createAccount,
 signInWithEmail,
 logoutUser,
 resetPassword,
 confirmPasswordResetWithCode,
 confirmPasswordResetWithToken,
 verifyResetToken,
 resendVerificationForUser,
 updateUserProfile,
 updateUserEmail,
 updateUserPassword,
 deleteUserAccount,
 getUserData,
 checkEmailVerified,
 checkEmailVerification,
 reauthenticateUser,
 checkUsernameAvailability,
 reserveUsername,
 sendCustomVerificationEmail,
 sendCustomPasswordResetEmail
};

export default authService;